import { apiCustomer } from '@/api'
import { EmptyState } from '@/components/feedback/EmptyState'
import { OrdersLoading } from '@/components/loaders'
import { Badge, ButtonLink, Card, DescriptionList, Heading, KeyValueRow, PageContainer, Price, Text } from "@/components/ui";
import { getStoredCompany } from '@/lib/company-storage'
import { ordersByCustomerQuery, sortOrdersByCreatedAtDesc } from '@/lib/order-history'
import { getOrderProgressSummary } from '@/lib/order-status'
import { usePageBackground } from '@/hooks/use-page-background'
import { useRequireAuth } from '@/hooks/use-require-auth'
import { OrderResponse } from '@/types/external'
import { format } from 'date-fns'
import { useEffect, useState } from 'react'
import { OrderItemLine, OrderItemList } from '@/components/orders/OrderItemLine'
import { toOrderItemLineProps } from '@/components/orders/order-item-line-props'
import { OrderRatingPrompt } from '@/components/orders/OrderRatingPrompt'
import type { OrderFeedback } from '@/domain/order-feedback/order-feedback'
import { useOrderFeedbacks } from '@/hooks/use-order-feedbacks'

export default function OrdersPage() {
  useRequireAuth()

  const [orders, setOrders] = useState<OrderResponse[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(false)

  useEffect(() => {
    async function loadOrders() {
      try {
        const company = getStoredCompany()

        if (!company) {
          setError(true)
          return
        }

        const { data } = await apiCustomer.get(
          `/orders/merchants/${company.id}/by-customer${ordersByCustomerQuery}`,
        )

        setOrders(sortOrdersByCreatedAtDesc(data))
      } catch (error) {
        console.error(error)
        setError(true)
      } finally {
        setLoading(false)
      }
    }

    loadOrders()
    const interval = setInterval(loadOrders, 60000)
    return () => clearInterval(interval)
  }, [])

  usePageBackground()
  const { feedbacks, remember } = useOrderFeedbacks(orders.map((order) => order.id))

  if (loading) return <OrdersLoading />

  if (error) {
    return (
      <EmptyState
        title="Não deu pra carregar seus pedidos"
        description="Tente de novo em alguns instantes."
      />
    )
  }

  if (orders.length === 0) {
    return (
      <EmptyState
        title="Você ainda não fez nenhum pedido"
        description="Faça seu primeiro pedido e ele aparecerá aqui."
        action={
          <ButtonLink to="/" size="lg" fullWidth>
            Ir para o cardápio
          </ButtonLink>
        }
      />
    )
  }

  return (
    <PageContainer top="header-gap" bottom="chrome">
      <Heading level={1} className="sr-only">
        Meus Pedidos
      </Heading>
      <ul className="flex flex-col gap-4">
        {orders.map((order) => (
          <li key={order.id}>
            <OrderCard order={order} feedback={feedbacks[order.id]} onRated={remember} />
          </li>
        ))}
      </ul>
    </PageContainer>
  )
}

function OrderCard({
  order,
  feedback,
  onRated,
}: {
  order: OrderResponse
  feedback?: OrderFeedback
  onRated: (feedback: OrderFeedback) => void
}) {
  const { label, progressTone, status } = getOrderProgressSummary(order)
  const displayId = order.displayId.toUpperCase()

  return (
    <Card as="article" padding="md">
      <header className="mb-3 flex items-baseline justify-between gap-3">
        <div className="min-w-0">
          <Heading level={2} size="section">
            Pedido #{displayId}
          </Heading>
          <Text size="sm" tone="muted">
            Realizado em {format(order.createdAt, "dd/MM 'às' HH:mm")}
          </Text>
        </div>
        <ButtonLink
          to={`/profile/orders/${order.id}`}
          variant="link"
          tone="primary"
          size="auto"
          className="shrink-0"
        >
          Ver mais
        </ButtonLink>
      </header>

      {status ? (
        <Badge tone={progressTone} variant="solid" uppercase>
          {label}
        </Badge>
      ) : null}

      <OrderRatingPrompt order={order} feedback={feedback} onRated={onRated} variant="badge" />

      <OrderItemList className="mt-4">
        {order.items.map((item, index) => (
          <OrderItemLine key={`${item.id}-${index}`} {...toOrderItemLineProps(item, { optionQuantitySuffix: 'x', hideFreePrice: true })} />
        ))}
      </OrderItemList>

      {order.total.orderAmount ? (
        <DescriptionList className="mt-4">
          <KeyValueRow
            emphasis="total"
            label="Total"
            value={<Price value={order.total.orderAmount.value} tone="secondary" />}
          />
        </DescriptionList>
      ) : null}
    </Card>
  )
}
